import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import debounce from 'lodash.debounce';
import { SketchPicker } from 'react-color';
// react-pintura
import { PinturaEditor } from '@pqina/react-pintura';
import { Canvg } from 'canvg';
// pintura
import '@pqina/pintura/pintura.css';

import {
  // editor
  createDefaultImageReader,
  createDefaultImageWriter,
  createDefaultShapePreprocessor,

  // plugins
  setPlugins,
  colorStringToColorArray,
  findNode,
  removeNode,
  getEditorDefaults,
  createDefaultFontSizeOptions,
  createDefaultTextAlignOptions,
  createMarkupEditorToolStyle,
  createMarkupEditorFontSizeControl,
  createMarkupEditorFontSizeOptions,
  markup_editor_defaults,
  createDefaultFontFamilyOptions,
  createDefaultFontStyleOptions,
  createMarkupEditorToolbar
} from '@pqina/pintura';
import { ImageColorPicker } from 'react-image-color-picker';

import { useDispatch, useSelector } from 'react-redux';
import { lomavisCreatorRootState } from '../../../../pages/lomavisCreator';
import { useTranslate } from '@tolgee/react';
import { getLanguage } from '../../../../locales/language';
import {
  deleteAnOverlay,
  editImageThumbnail,
  saveEditorFullImage,
  updateAnOverlay,
  updateOverlayCreatorCloseState,
  updateThumbnailProcessing
} from '../../../../redux/slices/lomavisCreator/lomavisCreator';
import {
  Button,
  ButtonGroup,
  Dropdown,
  Form,
  InputGroup,
  Overlay,
  OverlayTrigger,
  Popover
} from 'react-bootstrap';
import {
  calculateFontSize,
  calculateMinDimensions,
  deepEqual,
  getFirstWrappedLine,
  getImageDimensionRatio,
  getImageDimensions,
  getPinturaInputDimensionsByTextAndFontSize,
  getSvgAspectRatioFromUrl,
  getTextMetrics,
  getTextWidth,
  hexToNormalizedArray,
  normalizedArrayToHex,
  parsePercentageToThreeDecimalPlaces,
  rgbStringToHex,
  scaleDownImage,
  wrapText
} from '../../utils/helpers';
import { EditorImageConfig } from '../../../../redux/slices/lomavisCreator/utils';
import { ALL } from '../../../../config/constants';
import {
  convertLogo,
  convertLogoWithoutBg,
  convertSVGtoPNG,
  getLineMetrics
} from '../../utils/brandOverlay';
import AddElementDropdown from '../commonElements/editorAddDropdown';
import FontSizeSelector from '../commonElements/fontSizeSelector';
import {
  loadTemplateFont,
  resolveTemplateFont,
  templateShapeStyleControls,
  templateTextToolStyles
} from './templateFont';
import { isEqual } from 'lodash';
import TextAlignDropdown from '../commonElements/textAlignmentOptions';
import {
  LOMAVIS_TEMPLATE_POST_LOGO_PLACEHOLDER_RECTANGLE_ASPECT_RATIO,
  LOMAVIS_TEMPLATE_POST_LOGO_PLACEHOLDER_RECTANGLE_URL,
  LOMAVIS_TEMPLATE_POST_LOGO_PLACEHOLDER_SQUARE_ASPECT_RATIO,
  LOMAVIS_TEMPLATE_POST_LOGO_PLACEHOLDER_SQUARE_URL,
  LOMAVIS_TEMPLATE_POST_RECTANGLE_LOGO_PLACEHOLDER_ID,
  LOMAVIS_TEMPLATE_POST_SQUARE_LOGO_PLACEHOLDER_ID
} from '../../config/constants';
import { DEFAULT_EDITOR_FONT_URL } from '../contants';
import './overlay.css';

setPlugins(EditorImageConfig.plugins);

const getLocale = (lang) => {
  switch (lang) {
    case 'de':
    case 'de-DE':
      return {
        ...EditorImageConfig.DeLocale
      };
    case 'en':
    case 'en-GB':
    default:
      return {
        ...EditorImageConfig.EnLocale
      };
  }
};
const stringifyImageState = (imageState) => {
  return JSON.stringify(imageState, (k, v) => (v === undefined ? null : v));
};

const preprocessSVG = async (svgContent: string): Promise<Blob> => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const v = Canvg.fromString(ctx, svgContent);
  await v.render();
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob as Blob));
  });
};

const handleSVGUpload = (file: File): Promise<Blob> => {
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.onload = async (event) => {
      const svgString = event.target?.result as string;
      const rasterizedImage = await preprocessSVG(svgString); // Assuming preprocessSVG returns a Blob
      resolve(rasterizedImage);
    };
    reader.onerror = reject;
    reader.readAsText(file);
  });
};

export default function ImageEdit({
  fileInputRef,
  setStartSubmission,
  handleClose,
  handleCloseNoConfirmation,
  triggerFullEditorImage,

  inputRef,
  currentOverlay,
  bgImg,
  selectedImage
}) {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const [locale, setLocale] = useState(getLocale(getLanguage()));
  const editorRef = useRef(null);
  const editor2Ref = useRef(null);
  const buttonRef = useRef(null);

  const handleEditorInit = (editor) => {
    editorRef.current = editor;
  };

  const [addCrop, setAddCrop] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [storedImageState, setStoredImageState] = useState(
    '{"cropLimitToImage": false }'
  );

  const [initialLoads, setInitialLoads] = useState(true);
  const [initialLoadState, setInitialLoadState] = useState({});

  const [selectedColors, setSelectedColors] = useState({
    primaryColor: currentOverlay?.overlayData?.overlay?.primaryColor || '',
    secondaryColor: currentOverlay?.overlayData?.overlay?.secondaryColor || ''
  });
  const [toggleColorPicker, setToggleColorPicker] = useState(false);
  const [currentColor, setCurrentColor] = useState('');

  const [onFocusState, setOnFocusState] = useState(false);

  const [customTextSettings, setCustomTextSettings] = useState({
    underline: false,
    italize: false,
    bold: false,
    fontSize: 144,
    alignment: 'left',
    textColor: [0, 0, 0]
  });

  const [textColorPicker, setTextColorPicker] = useState(false);

  let duplicateSettings;

  const allCreatedOverlays = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.createdOverlayArray
  );
  const OverlayEditorChanged = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.allowOverlayCreatorClose
  );

  const postData = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.postData[ALL]
  );
  const { brandData } = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator
  );

  // Every text this editor measures, draws or offers has to agree on one
  // family: the overlay is rendered in the brand font later, so laying it out
  // in the default font here put the text at the wrong size and showed the
  // wrong face while doing it.
  const templateFont = resolveTemplateFont(brandData?.fonts);
  const { family: templateFontFamily, faceUrl: templateFontFaceUrl } =
    templateFont;
  const currentItem = allCreatedOverlays.find(
    (item) => item?.imageUuid === selectedImage
  );
  useEffect(() => {
    setLocale(getLocale(getLanguage()));
  }, [getLanguage()]);

  const editorDefaults = getEditorDefaults({
    ...EditorImageConfig.ImageEditorDefaults,
    utils: ['annotate', 'sticker'],

    locale
  });

  // inline
  const [result, setResult] = useState('');

  function removeObjectsByKeys(object, keysToRemove) {
    return Object.keys(object).reduce((result, key) => {
      if (!keysToRemove.includes(key)) {
        result[key] = object[key];
      }
      return result;
    }, {});
  }

  const getLastObject = (obj) => {
    const keys = Object.keys(obj);
    if (keys.length === 0) {
      return null; // or handle as needed
    }
    const lastKey = keys[keys.length - 1];
    return obj[lastKey];
  };

  const lastFontSizeRef = useRef<number | null>(null);
  const lastFontWeightRef = useRef<string | null>(null);
  const lastFontStyleRef = useRef<string | null>(null);
  const lastTextColorRef = useRef<any>(null);
  const lastTextAlignmentRef = useRef<string | null>(null);

  const focusIdRef = useRef<string | null>(null);

  const memoizedTextSettings = useMemo(
    () => customTextSettings,
    [customTextSettings]
  );

  const exportImage = async (imageStateJson) => {
    if (editorRef.current) {
      const editor = editorRef.current;

      // Start timing
      const startTime = performance.now();

      // Dispatch update to indicate processing has started
      setUpdating(true);

      const dimensions = await getImageDimensions(bgImg);

      if (editorRef.current?.history) {
        /*const prevEditorHistory =
          editorRef.current?.history?.get()[
            editorRef.current?.history?.get().length - 1
          ];*/
        console.log('editor update', imageStateJson);
        const prevEditorHistory = imageStateJson;

        const allTextAnnotations = prevEditorHistory?.annotation?.filter(
          (item) => item?.text
        );
        const allImagesAnnotations = prevEditorHistory?.annotation?.filter(
          (item) => item?.backgroundImage && item?.id !== 'overlay'
        );

        const selectedTexts = allTextAnnotations?.filter(
          (item) => item?.isSelected
        );

        if (selectedTexts?.length > 0) {
          if (selectedTexts[0].id !== focusIdRef.current) {
            focusIdRef.current = selectedTexts[0].id;
            setCustomTextSettings({
              underline: false,
              italize: selectedTexts[0].fontStyle === 'italic' ? true : false,
              bold: selectedTexts[0].fontWeight === 'bold' ? true : false,
              fontSize: selectedTexts[0].fontSize,
              textColor: selectedTexts[0].color,
              alignment: selectedTexts[0].textAlign
            });
            return;
          }

          const newFontSize =
            typeof customTextSettings.fontSize === 'number'
              ? customTextSettings.fontSize
              : parseInt(customTextSettings.fontSize, 10);
          const newFontWeight = customTextSettings?.bold ? 'bold' : 'normal';
          const newFontStyle = customTextSettings?.italize
            ? 'italic'
            : 'normal';
          const newColor = customTextSettings?.textColor;
          const newAlignment = customTextSettings?.alignment;

          if (!deepEqual(duplicateSettings, customTextSettings)) {
            // Only update fontSize if it changed'
            duplicateSettings = customTextSettings;

            const updatedAnnotes = selectedTexts?.map((item) => {
              return {
                ...item,
                fontSize: newFontSize,
                fontWeight: newFontWeight,
                fontStyle: newFontStyle,
                color: newColor,
                textAlign: newAlignment
              };
            });

            lastFontSizeRef.current = newFontSize;
            lastFontWeightRef.current = newFontWeight;
            lastFontStyleRef.current = newFontWeight;
            lastTextColorRef.current = newColor;
            lastTextAlignmentRef.current = newAlignment;

            // Replace existing annotations instead of duplicating

            editorRef.current.imageAnnotation = imageStateJson?.annotation.map(
              (annotation) => {
                const updatedAnnotation = updatedAnnotes.find(
                  (updated) => updated.id === annotation.id
                );
                return updatedAnnotation || annotation; // Replace if found, otherwise keep existing
              }
            );

            const filteredAnnotation = imageStateJson?.annotation.map(
              (annotation) => {
                const updatedAnnotation = updatedAnnotes.find(
                  (updated) => updated.id === annotation.id
                );
                return updatedAnnotation || annotation; // Replace if found, otherwise keep existing
              }
            );

            const updatedObject = {
              ...imageStateJson,
              annotation: filteredAnnotation
            };

            editorRef.current.editor.history.write(updatedObject);

            // editorRef.current.imageAnnotation = [
            //   ...imageStateJson.annotation,
            //   updatedAnnote
            // ];
            // Update last known fontSize

            let currentState = imageStateJson;
            let allAnnotations = currentState?.annotation;

            // Replace existing annotations instead of duplicating
            // allAnnotations = currentState?.annotation.map((annotation) => {
            //   const updatedAnnotation = updatedAnnotes.find(
            //     (updated) => updated.id === annotation.id
            //   );
            //   return updatedAnnotation || annotation; // Replace if found, otherwise keep existing
            // });

            // const imageStateStr = stringifyImageState({
            //   ...currentState,
            //   annotation: allAnnotations
            // });

            // console.warn('This ', {
            //   ...currentState,
            //   annotation: allAnnotations
            // });

            // setStoredImageState(imageStateStr);
          }
        }

        let logoArray = [];

        allImagesAnnotations?.forEach((item) => {
          const logoObj = {
            url: item?.backgroundImage,
            type:
              item?.id === LOMAVIS_TEMPLATE_POST_SQUARE_LOGO_PLACEHOLDER_ID ||
              LOMAVIS_TEMPLATE_POST_RECTANGLE_LOGO_PLACEHOLDER_ID
                ? item?.id
                : 'sticker',
            x: item?.x,
            y: item.y,
            width: item?.width,
            height: item?.height,
            bgColor: '#ffffff'
          };
          logoArray.push(logoObj);
        });

        const processTextAnnotations = async () => {
          const textArray = await Promise.all(
            allTextAnnotations?.map(async (item) => {
              let newText;
              if (item?.width && !item?.height) {
                newText = wrapText(
                  item?.text,
                  item?.width,
                  item?.fontSize,
                  item?.fontFamily
                );
              }

              const minDimension = calculateMinDimensions({
                text: newText ? newText : item?.text,
                fontFamily: item?.fontFamily,
                fontSize: item?.fontSize,
                fontStyle: item?.fontStyle,
                fontWeight: item?.fontWeight,
                lineHeight: item?.lineHeight
              });

              const divDimension = getPinturaInputDimensionsByTextAndFontSize(
                item?.text,
                item?.fontSize
              );

              const wrappedLine = wrapText(
                item?.text,
                item?.width - 2,
                item?.fontSize,
                templateFontFamily
              );
              const lines = wrappedLine.split('\n');

              // console.log(
              //   'get metric options',
              //   divDimension,
              //   item?.fontSize,
              //   lines,
              //   DEFAULT_EDITOR_FONT_FAMILY,
              //   item?.fontWeight,
              //   item?.fontStyle
              // );

              const textMetrics = await getLineMetrics(
                item?.fontSize,
                lines,
                templateFontFamily,
                item?.fontWeight,
                item?.fontStyle
              );

              // const plainTextMetrics = await getLineMetrics(
              //   item?.fontSize,
              //   lines,
              //   DEFAULT_EDITOR_FONT_FAMILY,
              //   'normal',
              //   item?.fontStyle
              // );

              return {
                text: item?.text,
                color: normalizedArrayToHex(item?.color),
                x:
                  (parsePercentageToThreeDecimalPlaces(item?.x) / 100) *
                  dimensions?.width,
                y:
                  (parsePercentageToThreeDecimalPlaces(item?.y) / 100) *
                  dimensions?.height,
                width: minDimension?.minWidth,
                height: minDimension?.minHeight,
                textMetrics: textMetrics,
                boxWidth: item?.width - 4,
                boxHeight: item?.height,
                flipX: item?.flipX,
                flipY: item?.flipY,
                textAlign: item?.textAlign ? item?.textAlign : 'left',
                fontStyle: item?.fontStyle,
                fontWeight: item?.fontWeight,
                baseFontFamily: item?.fontFamily || templateFontFamily,
                fontSize: item?.fontSize,
                lineHeight:
                  parsePercentageToThreeDecimalPlaces(item?.lineHeight) / 100
              };
            })
          );

          return textArray;
        };
        let textArray = await processTextAnnotations();

        if (!OverlayEditorChanged) {
          dispatch(
            updateOverlayCreatorCloseState({
              state: true
            })
          );
        }

        dispatch(
          updateAnOverlay({
            uuid: currentOverlay.imageUuid,
            attribute: 'overlayData',
            value: {
              position: postData.media?.findIndex(
                (item) => item.uuid === currentOverlay?.imageUuid
              ),

              image: currentOverlay?.imageUuid,
              overlay: {
                url: currentItem?.overlayImage,
                originalUrl: currentItem?.overlayImage,
                uuid: currentItem?.overlayUuid,
                primaryColor: selectedColors.primaryColor,
                secondaryColor: selectedColors.secondaryColor,
                originalHeight: dimensions?.height,
                originalWidth: dimensions?.width,
                align: 'center'
              },
              text: textArray,
              logo: logoArray
            }
          })
        );
      }
    }
  };

  const debouncedExportImage = useCallback(() => {
    exportImage(JSON.parse(storedImageState));
  }, [selectedColors, customTextSettings]);

  //stops mouse glitch when moving around objects on the editor canvas
  let isMouseDown = false;
  let lastImageState = null;

  document.addEventListener('mousedown', () => {
    isMouseDown = true;
  });

  document.addEventListener('mouseup', () => {
    isMouseDown = false;

    // If there's a pending update, execute it current mouse release
    if (lastImageState) {
      handleEditorUpdate(lastImageState);
      lastImageState = null; // Clear stored state
    }
  });

  const handleEditorUpdate = debounce(async (imageState) => {
    if (initialLoads) {
      initializeAnnotations();
      const allOverlayData = currentItem?.overlayData;
      setInitialLoadState(allOverlayData);
      setInitialLoads(false);
    }

    if (isMouseDown) {
      lastImageState = imageState; // Store the last imageState while mouse is down
      return;
    }

    const imageStateStr = stringifyImageState(imageState);
    setStoredImageState(imageStateStr);

    const imageStateJson = JSON.parse(imageStateStr);

    exportImage(imageStateJson);
  }, 100);

  const willRenderToolbar = (toolbar) => {
    // get the export button
    const exportButton = findNode('export', toolbar);

    removeNode('export', toolbar);

    return toolbar;
  };
  /////////////////////////
  const resetImageView = async () => {
    const editor = editorRef.current;

    // Utility function to get the last object in an array
    const getLastObject = (arr) => {
      if (!arr || arr.length === 0) return null;
      return arr[arr.length - 1];
    };
    const getFirstObject = (arr) => {
      if (!arr || arr.length === 0) return null;
      return arr[0];
    };

    // Utility function to remove specific keys from an object
    const removeObjectsByKeys = (obj, keys) => {
      const result = { ...obj };
      keys.forEach((key) => {
        delete result[key];
      });
      return result;
    };

    if (editor) {
      // Get the edited image state history
      const history = editor.history?.get();
      const imageState = getLastObject(history);
      let OGWidth;
      let OGHeight;
      let HistoryWidth = getFirstObject(history)?.crop.width;
      let HistoryHeight = getFirstObject(history)?.crop.height;

      await getImageDimensions(currentOverlay?.overlayImage)
        .then((dimensions) => {
          OGHeight = dimensions?.height;
          OGWidth = dimensions?.width;
        })
        .catch((error) => {
          console.error('Error loading image:', error);
        });

      if (imageState) {
        // Remove crop-related keys from the image state
        let editsWithoutCrop = removeObjectsByKeys(imageState, ['crop']);

        const currentCrop = imageState.crop;
        const editedState = {
          ...editsWithoutCrop,
          crop: {
            height: OGHeight,
            width: OGWidth,
            x: 0,
            y: 0
          }
        };

        // Write the modified image state back to the editor's history
        editorRef.current.history.write(editedState);

        editorRef.current.processImage(editedState).then((result) => {
          // Handle the resulting image data (base64 or blob)
          const editedImage = result.dest; // This will be a blob

          const url = URL.createObjectURL(editedImage);

          // Do something with the URL, e.g., display it, download it, etc.

          dispatch(
            updateAnOverlay({
              uuid: currentOverlay.imageUuid,
              attribute: 'overlayPreview',
              value: url
            })
          );

          setTimeout(() => {
            setStartSubmission(false);
            handleCloseNoConfirmation();
          }, 1000);
        });
      } else {
        console.log('No image state found in history.');
      }
    } else {
      console.log('Editor not initialized.');
    }
  };

  // const handleInputChange = async (e) => {
  //   const fileList = e.target.files; // This is a FileList
  //   const fileArray: File[] = Array.from(fileList); // Convert the FileList to an array

  //   const filePromises = fileArray.map(async (file: File) => {
  //     const cleanedFile = await handleSVGUpload(file);
  //     const previewImage = URL.createObjectURL(cleanedFile); // Assuming cleanedFile is a Blob

  //     setUploadedImage(previewImage);
  //     return cleanedFile;
  //   });

  //   // Wait for all files to be processed
  //   await Promise.all(filePromises);
  // };
  const handleInputChange = (e) => {
    // Exit if no files selected
    const fileList = e.target.files; // This is a FileList
    const fileArray = Array.from(fileList); // Convert the FileList to an array

    fileArray.map(async (file: any) => {
      let preview_image: any = URL.createObjectURL(file);
      let assetBlob: any = URL.createObjectURL(file);

      if (file) {
        try {
          const resizedBlob = await scaleDownImage(file);
          const url = URL.createObjectURL(resizedBlob);
          setUploadedImage(url);
        } catch (error) {
          console.error(error);
          setUploadedImage(preview_image);
        }
      }
    });

    fileInputRef.current.value = '';
  };

  const handleEditorProcess = async (res) => {
    // 1. Turn imageState into string, replaces undefined values with null
    const imageStateStr = stringifyImageState(res.imageState);
    setStoredImageState(imageStateStr);
  };

  const handleImageUpdate = async ({
    uploadedImage,
    storedImageState,
    coordinate = null,
    availableDimension = null,
    additionalObj = {},
    id = uploadedImage,
    aspectRatio = undefined,
    returnAnnote = false
  }) => {
    if (!storedImageState) {
      return;
    }

    // const dimensions = availableDimension
    //   ? availableDimension
    //   : await getImageDimensions(uploadedImage);

    const editorDimensions = await getImageDimensions(bgImg);

    const currentState = JSON.parse(storedImageState);
    const justAnnotations = [...(currentState?.annotation || [])];

    const filteredState = removeObjectsByKeys(currentState, ['annotation']);
    const newAnnote = {
      aspectRatio: aspectRatio,
      backgroundColor: [0, 0, 0, 0],
      backgroundImage: uploadedImage,
      backgroundSize: 'contain',
      cornerRadius: 0,
      disableErase: true,
      disableStyle: [
        'cornerRadius',
        'backgroundColor',
        'strokeColor',
        'strokeWidth'
      ],
      disableTextScale: false,
      height: availableDimension
        ? availableDimension?.height
        : id === 'rectangle'
        ? (editorDimensions?.width / 3) * 0.6
        : editorDimensions?.width / 3,
      id: id,
      isEditing: false,
      isSelected: true,
      opacity: 1,
      rotation: 0,
      strokeColor: [0, 0, 0],
      strokeWidth: 0,
      width: availableDimension
        ? availableDimension?.width
        : editorDimensions?.width / 3,
      x: coordinate ? coordinate.x : 0,
      y: coordinate ? coordinate.y : 0,
      ...additionalObj
    };
    if (returnAnnote) {
      return newAnnote;
    }

    const combined = {
      ...filteredState,
      annotation: [...justAnnotations, newAnnote]
    };

    const imageStateStr = stringifyImageState(combined);
    editorRef.current.history.write(JSON.parse(imageStateStr));
    setStoredImageState(imageStateStr);

    editorRef.current.util = undefined;
    editorRef.current.annotateActiveTool = undefined;
    editorRef.current.util = 'sticker';
    editorRef.current.util = 'annotate';

    editorRef.current.util = 'sticker';
  };

  const handleChange = (e) => {
    const { id, value } = e.target;

    // Remove all # characters if present, then add a single # at the beginning
    const formattedValue = `#${value.replace(/^#+/, '')}`;

    setSelectedColors((prev) => ({
      ...prev,
      [id]: formattedValue // Update based on the input's id
    }));
  };
  const handleColorPick = (color: string) => {
    setSelectedColors((prev) => ({
      ...prev,
      [currentColor]: rgbStringToHex(color) // Update based on the input's id
    }));

    setCurrentColor('');
    setToggleColorPicker(false);
  };
  const getRandomPercentage = () => {
    return `${25 + Math.random() * 50}%`; // Generates a random number between 25% and 75%
  };
  const handleAddText = ({
    fontWeight = customTextSettings?.bold ? 'bold' : 'normal',
    fontStyle = customTextSettings?.italize ? 'italic' : 'normal',
    fontSize = typeof customTextSettings?.fontSize === 'number'
      ? customTextSettings?.fontSize
      : parseInt(customTextSettings?.fontSize),
    textColor = customTextSettings?.textColor
  }) => {
    if (editorRef.current) {
      const updatedAnnotationList = [
        ...editorRef.current.imageAnnotation,
        {
          x: getRandomPercentage(),
          y: getRandomPercentage(),
          text: 'Text',
          fontWeight: fontWeight,
          fontStyle: fontStyle,
          fontSize: fontSize,
          width: getTextWidth(
            'Textss', // we use here some wider text, because it was often cut off. this is why we just a slightly wider text
            fontSize,
            templateFontFamily,
            customTextSettings?.bold ? 'bold' : 'normal'
          ),
          color: textColor,
          id: `T-${Date()}`,
          isEditing: false,
          isSelected: true,
          fontFamily: templateFontFamily,
          disableErase: true,
          disableNewline: false,
          textAlign: customTextSettings?.alignment,
          disableTextScale: false
        }
      ];
      editorRef.current.util = undefined;
      editorRef.current.annotateActiveTool = undefined;
      editorRef.current.annotateActiveTool = 'move';

      // this avoid glitching when adding text in the pintura menu
      editorRef.current.util = 'annotate';
      editorRef.current.util = 'sticker';
      editorRef.current.util = 'annotate';

      editorRef.current.annotateActiveTool = 'move';

      editorRef.current.imageAnnotation = updatedAnnotationList;
    }
  };

  const initializeAnnotations = async () => {};
  const uploadMainOverlay = async () => {
    const editorDimension = await getImageDimensions(bgImg); // { width, height }

    const overlayBlob = await convertSVGtoPNG(
      currentOverlay?.overlayImage,
      editorDimension?.width
    );

    const editorHistory =
      editorRef.current.history.get()[
        editorRef.current.history.get().length - 1
      ];

    const overlayDimension = await getImageDimensions(
      currentOverlay?.overlayImage
    ); // { width, height }

    const editorAspectRatio = editorDimension.width / editorDimension.height;
    const overlayAspectRatio = overlayDimension.width / overlayDimension.height;

    let scaledWidth, scaledHeight;

    // Scale overlay to fit within editor dimensions while maintaining aspect ratio
    if (overlayAspectRatio > editorAspectRatio) {
      // Overlay is wider than editor; fit based on width
      scaledWidth = editorDimension.width;
      scaledHeight = scaledWidth / overlayAspectRatio;
    } else {
      // Overlay is taller than editor; fit based on height
      scaledHeight = editorDimension.height;
      scaledWidth = scaledHeight * overlayAspectRatio;
    }

    // Center the overlay within the editor
    const centeredX = (editorDimension.width - scaledWidth) / 2;
    const centeredY = (editorDimension.height - scaledHeight) / 2;
    const overlayAnnote = await handleImageUpdate({
      uploadedImage: URL.createObjectURL(overlayBlob),
      storedImageState: stringifyImageState(editorHistory),

      coordinate: {
        x: centeredX,
        y: centeredY
      },
      availableDimension: {
        width: scaledWidth,
        height: scaledHeight
      },
      additionalObj: {
        disableSelect: true,
        disableManipulate: true,
        disableMove: true,
        disableResize: true,
        disableRotate: true
      },
      id: 'overlay',
      returnAnnote: true
    });

    const allOverlayData = initialLoadState;

    const currentState = editorHistory;
    const justAnnotations = [...(currentState?.annotation || [])];

    const filteredState = removeObjectsByKeys(currentState, ['annotation']);
    const logoAnnotes =
      (await processLogos(allOverlayData, currentState)) || [];
    const textAnnotes = (await updateTexts(allOverlayData, currentState)) || [];
    const combined = {
      ...filteredState,
      annotation: [
        overlayAnnote,
        ...justAnnotations,
        ...logoAnnotes,
        ...textAnnotes
      ]
    };
    const imageStateStr = stringifyImageState(combined);
    editorRef.current.history.write(JSON.parse(imageStateStr));
    setStoredImageState(imageStateStr);
    editorRef.current.util = undefined;
    editorRef.current.util = 'annotate';
    editorRef.current.annotateActiveTool = 'move';
  };

  async function processLogos(allOverlayData, currentState) {
    try {
      const logoUrls = allOverlayData.logo?.map((item) => item?.url);
      const editorHistory =
        editorRef.current.history.get()[
          editorRef.current.history.get().length - 1
        ];

      // You can now use the blobs, for example, create Object URLs for preview
      let allAnnotes = [];
      await logoUrls?.forEach(async (url, index) => {
        const newAnnotate = await handleImageUpdate({
          uploadedImage: url,
          storedImageState: stringifyImageState(currentState),

          coordinate: {
            x: allOverlayData?.logo[index].x,
            y: allOverlayData?.logo[index].y
          },
          availableDimension: {
            width: allOverlayData?.logo[index].width,
            height: allOverlayData?.logo[index].height
          },
          aspectRatio:
            allOverlayData?.logo[index].width /
            allOverlayData?.logo[index].height,
          id: allOverlayData?.logo[index].type,
          returnAnnote: true
        });

        allAnnotes.push(newAnnotate);
      });
      return allAnnotes;
    } catch (error) {
      console.error('Error converting logos:', error);
    }
  }

  const updateTexts = async (allOverlayData, currentState) => {
    // Wait for the face, don't just ask for it: calculateFontSize below
    // measures in `templateFontFamily`.
    await loadTemplateFont(templateFont);

    const ImageDimensions = await getImageDimensions(bgImg);

    const textInfoArray = allOverlayData?.text || [];
    const newTextAnnotation = [];
    for (const textItem of textInfoArray) {
      const resultFontSize = calculateFontSize(
        textItem.text,
        templateFontFamily,
        textItem.fontStyle,
        textItem.fontWeight,
        textItem.lineHeight,
        textItem.fontSize,
        textItem.width,
        textItem.height,
        textItem.textAlign
      );

      const newAnnote = {
        x: `${(textItem.x / ImageDimensions.width) * 100}%`,
        y: `${(textItem.y / ImageDimensions.height) * 100}%`,
        text: textItem.text,
        color: hexToNormalizedArray(textItem.color),
        fontSize: textItem?.boxWidth ? textItem.fontSize : resultFontSize,
        fontFamily: templateFontFamily,
        fontWeight: textItem.fontWeight,
        fontStyle: textItem.fontStyle,
        lineHeight: `${textItem.lineHeight * 100}%`,
        rotation: 0,
        flipX: textItem.flipX ? textItem.flipX : false,
        flipY: textItem.flipY ? textItem.flipY : false,
        opacity: 1,
        id: new Date(),
        disableErase: true,
        fontVariant: 'normal',
        format: 'text',
        textAlign: textItem.textAlign,
        isSelected: false,
        isEditing: false,
        disableNewline: false,
        disableTextScale: false,
        width: textItem?.boxWidth
      };

      newTextAnnotation.push(newAnnote);
    }

    return newTextAnnotation;
  };

  // useEffect(() => {
  //   handleImageUpdate({
  //     uploadedImage: uploadedImage,
  //     storedImageState: storedImageState
  //   });
  // }, [uploadedImage]);

  useEffect(() => {
    if (selectedColors.primaryColor || selectedColors.secondaryColor) {
      debouncedExportImage();
    }
  }, [selectedColors]);

  useEffect(() => {
    // if (!isEqual(memoizedTextSettings, customTextSettings)) {
    debouncedExportImage();
    setTimeout(() => debouncedExportImage(), 500);
  }, [memoizedTextSettings]);

  useEffect(() => {
    setSelectedColors({
      primaryColor: currentOverlay?.overlayData?.overlay?.primaryColor || '',
      secondaryColor: currentOverlay?.overlayData?.overlay?.secondaryColor || ''
    });
  }, [bgImg]);

  const [overlayRatio, setOverlayRatio] = useState(1);
  useEffect(() => {
    getSvgAspectRatioFromUrl(currentOverlay?.overlayImage)
      .then((ratio) => {
        setOverlayRatio(ratio);
      })
      .catch((error) => console.error(error));
  }, [currentOverlay?.overlayImage]);

  const [imageDisplaySize, setImageDisplaySize] = useState({
    width: 600,
    height: 600
  });

  useEffect(() => {
    const parentEl = document.querySelector('.overlay_Editor');
    if (!parentEl) return;

    let resizeObserver: ResizeObserver | null = null;

    const observeSize = (targetEl: HTMLElement) => {
      resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          const { width, height } = entry.contentRect;

          const roundedWidth = Math.round(width);
          const roundedHeight = Math.round(height);
          if (roundedWidth > 0 && roundedHeight > 0) {
            // console.log('image display', roundedWidth, roundedHeight);
            setImageDisplaySize({ width: roundedWidth, height: roundedHeight });
          }
        }
      });

      resizeObserver.observe(targetEl);
    };

    // Try to find the element immediately
    const initialTarget = parentEl.querySelector('.PinturaShapeEditor');
    if (initialTarget) {
      observeSize(initialTarget as HTMLElement);
    }

    // Watch for future additions using MutationObserver
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (
            node instanceof HTMLElement &&
            node.classList.contains('PinturaShapeEditor')
          ) {
            observeSize(node);
          }

          // In case it's deeper inside
          const foundChild = (node as HTMLElement)?.querySelector?.(
            '.PinturaShapeEditor'
          );
          if (foundChild) {
            observeSize(foundChild as HTMLElement);
          }
        }
      }
    });

    mutationObserver.observe(parentEl, {
      childList: true,
      subtree: true
    });

    return () => {
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
    };
  }, []);

  return (
    <div
      style={{ overflowY: 'scroll' }}
      className="overlay_Editor w-100 h-100 d-flex flex-column justify-content-center PinturaUtilFooter"
    >
      {!toggleColorPicker && (
        <div className="p-2 mt-3 d-flex justify-content-between align-items-center w-100">
          <div className="d-flex justify-content-start align-items-center gap-2">
            <div>
              <AddElementDropdown
                addSquareLogo={() => {
                  handleImageUpdate({
                    uploadedImage:
                      LOMAVIS_TEMPLATE_POST_LOGO_PLACEHOLDER_SQUARE_URL,
                    storedImageState: storedImageState,
                    id: LOMAVIS_TEMPLATE_POST_SQUARE_LOGO_PLACEHOLDER_ID,
                    aspectRatio:
                      LOMAVIS_TEMPLATE_POST_LOGO_PLACEHOLDER_SQUARE_ASPECT_RATIO
                  });
                }}
                addRectLogo={() => {
                  handleImageUpdate({
                    uploadedImage:
                      LOMAVIS_TEMPLATE_POST_LOGO_PLACEHOLDER_RECTANGLE_URL,
                    storedImageState: storedImageState,
                    id: LOMAVIS_TEMPLATE_POST_RECTANGLE_LOGO_PLACEHOLDER_ID,
                    aspectRatio:
                      LOMAVIS_TEMPLATE_POST_LOGO_PLACEHOLDER_RECTANGLE_ASPECT_RATIO
                  });
                }}
                addTitle={() => {
                  handleAddText({
                    fontWeight: 'bold',
                    fontSize: 192,
                    textColor: [0, 0, 0]
                  });
                }}
                addSecTitle={() => {
                  handleAddText({
                    fontWeight: 'bold',
                    fontSize: 128,
                    textColor: [0, 0, 0]
                  });
                }}
                addParagraph={() => {
                  handleAddText({
                    fontWeight: 'normal',
                    fontSize: 96,
                    textColor: [0, 0, 0]
                  });
                }}
              />
            </div>
            <FontSizeSelector
              fontSize={customTextSettings?.fontSize}
              onChange={(size: number) => {
                setCustomTextSettings((prevState) => ({
                  ...prevState,
                  fontSize: size
                }));
              }}
            />
            <div>
              <Button
                ref={buttonRef} // Attach ref for positioning the popover
                style={{
                  aspectRatio: 1,
                  width: '40px'
                }}
                variant="light"
                className="p-0 px-1 mx-1 position-relative d-flex justify-content-center align-items-center p-2"
                onClick={() => setTextColorPicker((prev) => !prev)} // Toggle on click
              >
                <i
                  className="fa-duotone fa-a fs-6 pr-0 mb-2"
                  style={{
                    position: 'relative',
                    paddingBottom: '1px',
                    paddingRight: 0
                  }}
                ></i>
                <span
                  className="mt-2"
                  style={{
                    position: 'absolute',
                    bottom: '2px',
                    left: '0',
                    right: '0',
                    height: '3px',
                    backgroundColor: normalizedArrayToHex(
                      customTextSettings?.textColor
                    ),
                    borderRadius: '2px'
                  }}
                />
              </Button>

              <Overlay
                show={textColorPicker}
                target={buttonRef.current}
                placement="bottom"
                rootClose
              >
                <Popover id="popover-color-picker">
                  <Popover.Body>
                    <SketchPicker
                      color={customTextSettings?.textColor}
                      onChange={(color) => {
                        const rgba = color.rgb;
                        const normalizedColor = [
                          rgba.r / 255,
                          rgba.g / 255,
                          rgba.b / 255,
                          rgba.a ?? 1
                        ]; // Normalize values

                        setCustomTextSettings((prevState) => ({
                          ...prevState,
                          textColor: normalizedColor
                        }));
                        setTextColorPicker(false);
                      }}
                    />
                    <button
                      className={'btn btn-secondary btn-sm mt-5 w-100'}
                      onClick={() => {
                        setTextColorPicker(false);
                      }}
                    >
                      {t('lomavisCreator.imageColorPicker.close')}
                    </button>
                  </Popover.Body>
                </Popover>
              </Overlay>
            </div>

            <Button
              variant={'light'}
              style={{
                aspectRatio: 1,
                width: '40px'
              }}
              className={`d-flex justify-content-center align-items-center p-2 ${
                customTextSettings.bold && 'border border-2 border-dark'
              }`}
              onClick={() => {
                setCustomTextSettings((prevState) => ({
                  ...prevState,
                  bold: !prevState.bold
                }));
              }}
            >
              <i
                style={{
                  paddingRight: 0
                }}
                className="fa-duotone fa-bold fs-6"
              ></i>
            </Button>

            <Button
              variant={'light'}
              style={{
                aspectRatio: 1,
                width: '40px'
              }}
              className={`d-flex justify-content-center align-items-center p-2 ${
                customTextSettings.italize && 'border border-2 border-dark'
              }`}
              onClick={() => {
                setCustomTextSettings((prevState) => ({
                  ...prevState,
                  italize: !prevState.italize
                }));
              }}
            >
              <i
                style={{
                  paddingRight: 0
                }}
                className="fa-duotone fa-italic fs-6"
              ></i>
            </Button>

            <TextAlignDropdown
              alignment={customTextSettings?.alignment}
              onChange={(alignment) => {
                setCustomTextSettings((prevState) => ({
                  ...prevState,
                  alignment: alignment
                }));
              }}
            />
          </div>
          <div>
            <Button
              variant="secondary"
              style={{
                aspectRatio: 1,
                width: '40px'
              }}
              className="d-flex justify-content-center align-items-center p-2"
              onClick={() => {
                dispatch(
                  deleteAnOverlay({
                    uuid: currentOverlay?.imageUuid
                  })
                );
              }}
            >
              <i
                style={{
                  paddingRight: 0
                }}
                className="fa-duotone fa-solid fa-trash fs-6"
              ></i>
            </Button>
          </div>
        </div>
      )}

      <link href={DEFAULT_EDITOR_FONT_URL} rel="stylesheet"></link>
      <link href={templateFontFaceUrl} rel="stylesheet"></link>

      <div
        className="w-100"
        style={{ height: '100%', overflowY: 'auto', overflow: 'hidden' }}
      >
        {toggleColorPicker && (
          <div className="d-flex w-100 h-100 justify-content-center flex-column gap-1 p-3">
            <div className="text-muted text-center mb-1 mt-2">
              {t('lomavisCreator.imageColorPicker.text')}
            </div>
            <div className="d-flex justify-content-end">
              <span
                className="cursor-pointer"
                onClick={() => {
                  setCurrentColor('');
                  setToggleColorPicker(false);
                }}
              >
                {t('lomavisCreator.close')}
              </span>
            </div>
            <div className="w-100 flex-fill d-flex justify-content-center align-items-center">
              <div
                className="mx-auto d-flex justify-content-center align-items-center"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',

                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    className="checkerboard-bg image-picker-wrapper flex-fill"
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      height:
                        overlayRatio > 1
                          ? '100%'
                          : `${imageDisplaySize.height}px`,
                      maxWidth: `${imageDisplaySize.width}px`,
                      maxHeight: `${imageDisplaySize.height}px`,
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <ImageColorPicker
                      onColorPick={handleColorPick}
                      imgSrc={currentOverlay?.overlayImage}
                      zoom={1}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div
          className="w-100 h-100"
          style={{ display: toggleColorPicker && 'none' }}
        >
          <PinturaEditor
            {...editorDefaults}
            ref={editorRef}
            stickers={[['Markers', []]]}
            cropSelectPresetOptions={[
              [9 / 16, '9:16'],
              [1, '1:1'],
              [5 / 4, '5:4'],
              [16 / 9, '16:9'],
              [undefined, t('lomavisCreator.editorCropRatio.Custom')]
            ]}
            enableToolbar={false}
            // markupEditorToolbar={[
            //   [
            //     'text',
            //     'Text',
            //     {
            //       disabled: true,
            //       options: [
            //         'bold',
            //         'italic',
            //         'underline',
            //         'strike',
            //         'fontSize',
            //         'fontFamily',
            //         'color',
            //         'backgroundColor',
            //         'alignment',
            //         'lineHeight',
            //         'letterSpacing'
            //       ],
            //       textAlign: createDefaultTextAlignOptions()
            //     }
            //   ],
            //   ['sharpie', 'Sharpie', { disabled: true }],
            //   ['eraser', 'Eraser', { disabled: true }]
            // ]}
            markupEditorToolbar={createMarkupEditorToolbar([
              'text',
              'line',
              'arrow',
              'rectangle',
              'ellipse',
              'preset'
            ])}
            markupEditorToolStyles={templateTextToolStyles({
              family: templateFontFamily,
              fontSize: customTextSettings?.fontSize,
              color: customTextSettings?.textColor
            })}
            enableTapToAddText={false}
            enableAutoSelectMoveTool={true}
            enableMoveTool={true}
            markupEditorToolRetainStyles={true}
            src={bgImg}
            onInit={handleEditorInit}
            onUpdate={handleEditorUpdate}
            onReady={uploadMainOverlay}
            enableZoom={false}
            enableZoomControls={false}
            cropInteractionFocus={'selection'}
            willRenderToolbar={willRenderToolbar}
            stickerStickToImage={true}
            onProcess={handleEditorProcess}
            markupEditorShapeStyleControls={templateShapeStyleControls(
              templateFontFamily
            )}
            // enableUtils={false}
            // enableToolbar={false}
            // enableButtonExport={false}
            // enableButtonRevert={false}
          ></PinturaEditor>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        max={1}
        style={{ display: 'none' }}
      />
      {/* <div
        style={{ display: 'none' }}
        className="w-100 d-flex gap-2 justify-content-end p-2"
      >
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            dispatch(
              deleteAnOverlay({
                uuid: currentOverlay?.imageUuid
              })
            );
          }}
        >
          {t('lomavisCreator.overlay.delete_overlay')}
        </Button>{' '}
        <Dropdown as={ButtonGroup}>
          <Dropdown.Toggle variant="secondary" size="sm">
            {t('lomavisCreator.overlay.add_logo')}
          </Dropdown.Toggle>

          <Dropdown.Menu>
            <Dropdown.Item
              onClick={() => {
                handleImageUpdate({
                  uploadedImage:
                    'https://placehold.co/1000x1000/000000/FFF?text=Logo',
                  storedImageState: storedImageState,
                  id: 'square'
                });
              }}
            >
              {t('lomavisCreator.dropdown.square')}
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => {
                handleImageUpdate({
                  uploadedImage:
                    'https://placehold.co/1000x600/000000/FFF?text=Logo',
                  storedImageState: storedImageState,
                  id: 'rectangle'
                });
              }}
            >
              {t('lomavisCreator.dropdown.rectangle')}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            handleAddText({});
          }}
        >
          {t('lomavisCreator.overlay.add_text')}
        </Button>
      </div> */}

      <div className="w-100 d-flex flex-column gap-2 p-2">
        <div className="w-100 d-flex justify-content-between align-items-center">
          <h3>{t('lomavisCreator.overlay.colors')}</h3>{' '}
          <p
            className={'text-muted cursor-pointer'}
            onClick={() => {
              setSelectedColors({
                primaryColor: '',
                secondaryColor: ''
              });
              setToggleColorPicker(false);
              setCurrentColor('');
            }}
          >
            {' '}
            {t('lomavisCreator.overlay.clear')}
          </p>
        </div>

        <p className={'text-muted'}>
          {t('lomavisCreator.overlay.colorsHelpText')}
        </p>
        <div className="w-100 d-flex justify-content-between gap-5 p-1">
          <div className="w-75 d-flex flex-column">
            <div style={{ marginLeft: '50px' }}>
              <Form.Label className="ml-5" htmlFor="primaryColor">
                {t('lomavisCreator.overlay.primary_color')}
              </Form.Label>
            </div>

            <InputGroup className="mb-3 w-100 d-flex gap-2">
              <div
                style={{
                  aspectRatio: 1,
                  height: '100%',
                  backgroundColor: selectedColors.primaryColor
                }}
                className=" border rounded-3 cursor-pointer "
                onClick={() => {
                  setToggleColorPicker(!toggleColorPicker);
                  setCurrentColor('primaryColor');
                }}
              ></div>
              <Form.Control
                placeholder="e.g #ffffff"
                aria-label="Primary color"
                aria-describedby="basic-addon1"
                id="primaryColor"
                value={selectedColors.primaryColor}
                onChange={handleChange}
              />
            </InputGroup>
            <div
              style={{ marginLeft: '50px' }}
              className="w-100 d-flex justify-content-start ml-5"
            >
              <span
                className={`badge badge-secondary cursor-pointer  ${
                  toggleColorPicker && currentColor === 'primaryColor'
                    ? 'text-primary'
                    : ''
                }`}
                onClick={() => {
                  setToggleColorPicker(!toggleColorPicker);
                  setCurrentColor('primaryColor');
                }}
              >
                {t('lomavisCreator.overlay.select_from_image')}
              </span>
            </div>
          </div>

          <div className="w-75 d-flex flex-column">
            <div style={{ marginLeft: '50px' }}>
              <Form.Label className="ml-5" htmlFor="secondaryColor">
                {t('lomavisCreator.overlay.secondary_color')}
              </Form.Label>
            </div>

            <InputGroup className="mb-3 w-100 d-flex gap-2">
              <div
                style={{
                  aspectRatio: 1,
                  height: '100%',
                  backgroundColor: selectedColors.secondaryColor
                }}
                onClick={() => {
                  setToggleColorPicker(!toggleColorPicker);
                  setCurrentColor('secondaryColor');
                }}
                className="border rounded-3 cursor-pointer"
              ></div>
              <Form.Control
                placeholder="e.g #ffffff"
                aria-label="Secondary color"
                aria-describedby="basic-addon1"
                id="secondaryColor"
                value={selectedColors.secondaryColor}
                onChange={handleChange}
              />
            </InputGroup>

            <div
              style={{ marginLeft: '50px' }}
              className="w-100 d-flex justify-content-start ml-5"
            >
              <span
                className={`badge badge-secondary cursor-pointer ${
                  toggleColorPicker && currentColor === 'secondaryColor'
                    ? 'text-primary'
                    : ''
                }`}
                onClick={() => {
                  setToggleColorPicker(!toggleColorPicker);
                  setCurrentColor('secondaryColor');
                }}
              >
                {t('lomavisCreator.overlay.select_from_image')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
